import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import EmployerViewController from "../../redux/actions/EmployerApiAction";
import EmployerInformationForm from "./tabs/EmployerForm";
import EmployerPermissionsTab from "./tabs/PermissionForm";
import MasterViewController from "../../redux/actions/masterApiActions";
import { getUserInfoFromToken } from "../../utils/authUtils";
import defaultPermissionsData from "../../JsonFile/PermisssionJsonfile.json";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx } from "../../components/auth/authTheme";

const DRAWER_WIDTH = { xs: "100%", sm: 560, md: 680 };

function EmployerFormModal({ show, handleClose, initialData = null }) {
  const [userType, setUserType] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const dispatch = useDispatch();
  const { addEmployer, updateEmployer } = EmployerViewController();
  const { getPermissionsModules, getRoles } = MasterViewController();
  const { permission_modules } = useSelector((state) => state.master);
  const { isLoding } = useSelector((state) => state.employer);
  const isEdit = Boolean(initialData?.id);
  const formikRef = useRef();

  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    company: "",
  });

  useEffect(() => {
    const { userType } = getUserInfoFromToken();
    setUserType(userType);
  }, []);

  useEffect(() => {
    dispatch(getRoles());
    if (userType) {
      dispatch(getPermissionsModules(userType));
    }
  }, [dispatch, userType]);

  const { roles } = useSelector((state) => state.master);
  
  // Calculate basePermissions for the Employer role
  const [basePermissions, setBasePermissions] = useState([]);

  useEffect(() => {
    if (roles && roles.length > 0 && permission_modules?.length) {
      const employerRole = roles.find(r => r.role_name.toLowerCase() === "employer");
      const rolePerms = employerRole?.permissions || [];
      
      const calculatedBase = permission_modules.map((mod) => {
        const found = rolePerms.find((p) => p.module === mod.key);
        // Determine if enabled
        let enabled = false;
        if (found) {
           enabled = mod.actions.some(act => found[act.key]);
        }
        const row = {
          module: mod.key,
          moduleLabel: mod.label,
          enabled,
          ...(found || {})
        };
        return row;
      });
      setBasePermissions(calculatedBase);
    }
  }, [roles, permission_modules]);

  useEffect(() => {
    if (show && initialData) {
      setInitialValues({
        first_name: initialData.first_name || "",
        last_name: initialData.last_name || "",
        email: initialData.email || "",
        mobile_number: initialData.mobile_number || "",
        company: initialData.company?.id || "",
      });
    } else if (show && !initialData) {
      setInitialValues({
        first_name: "",
        last_name: "",
        email: "",
        mobile_number: "",
        company: "",
      });
      setTabIndex(0);
    }
  }, [show, initialData]);

  useEffect(() => {
    if (!show) return;

    if (initialData?.permissions?.permissions?.length) {
      const patchedData = basePermissions.map((masterItem) => {
        const incomingItem = initialData.permissions.permissions.find(
          (i) => i.module === masterItem.module
        );
        return incomingItem ? { ...masterItem, ...incomingItem } : masterItem;
      });
      setPermissions(patchedData);
    } else if (basePermissions?.length) {
      // Just clone the basePermissions array so we can edit extras
      setPermissions(basePermissions.map(p => ({...p})));
    }
  }, [show, initialData, basePermissions]);

  const validationSchema = Yup.object({
    first_name: Yup.string().trim().required("First name is required"),
    last_name: Yup.string().trim().required("Last name is required"),
    email: Yup.string()
      .trim()
      .email("Please enter a valid email address")
      .nullable()
      .test(
        "email-or-mobile",
        "Email or Mobile Number is required",
        function (value) {
          return !!(value || this.parent.mobile_number);
        }
      ),
    mobile_number: Yup.string()
      .matches(/^[0-9]{10}$/, "Enter a valid 10-digit phone number")
      .nullable()
      .test(
        "email-or-mobile",
        "Email or Mobile Number is required",
        function (value) {
          return !!(value || this.parent.email);
        }
      ),
    company: Yup.string().required("Company is required"),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = {
        ...values,
        company: values.company,
        permissions: { permissions },
      };

      const action = isEdit
        ? updateEmployer(initialData.id, payload)
        : addEmployer(payload);

      await dispatch(action);
      resetForm();
      setTabIndex(0);
      handleClose();
    } catch (err) {
      console.error("Submission error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const validateBasicTab = async () => {
    if (!formikRef.current) return false;

    const errors = await formikRef.current.validateForm();
    formikRef.current.setTouched({
      first_name: true,
      last_name: true,
      email: true,
      mobile_number: true,
      company: true,
    });

    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    const valid = await validateBasicTab();
    if (valid) setTabIndex(1);
  };

  const handleTabChange = async (_event, newIndex) => {
    if (newIndex === 1) {
      const valid = await validateBasicTab();
      if (valid) setTabIndex(newIndex);
    } else {
      setTabIndex(newIndex);
    }
  };

  const handleCloseDrawer = () => {
    setTabIndex(0);
    handleClose();
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting, ...formikProps }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleCloseDrawer}
              width={DRAWER_WIDTH}
              title={isEdit ? "Edit Employer" : "Add Employer"}
              subtitle={
                tabIndex === 0
                  ? "Basic information"
                  : "Set module permissions"
              }
              footer={
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleCloseDrawer}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    disabled={tabIndex === 0 || isSaving}
                    onClick={() => setTabIndex((prev) => prev - 1)}
                  >
                    Back
                  </Button>
                  {tabIndex < 1 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      sx={primaryButtonSx}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      onClick={() => formikRef.current?.submitForm()}
                      sx={{
                        ...primaryButtonSx,
                        minWidth: 120,
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? "#FF6A00" : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving
                        ? isEdit
                          ? "Updating..."
                          : "Creating..."
                        : isEdit
                          ? "Update"
                          : "Submit"}
                    </Button>
                  )}
                </>
              }
            >
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Basic Information" />
                <Tab label="Permissions" />
              </Tabs>

              <Box>
                {tabIndex === 0 && (
                  <EmployerInformationForm formik={formikProps} />
                )}
                {tabIndex === 1 && (
                  <EmployerPermissionsTab
                    formik={formikProps}
                    modulesData={permission_modules}
                    permissions={permissions}
                    setPermissions={setPermissions}
                    basePermissions={basePermissions}
                  />
                )}
              </Box>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default EmployerFormModal;
